import { useRef } from "react";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import { BiArrowBack } from "react-icons/bi";
import { IoCloudDownloadOutline } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
    getGrade,
    getRemark,
    getStudentClassStatus,
    getSubjectFromcards,
} from "../../../shared/utils/functions";
import { selectSchoolDetails } from "../../../redux/slices/school/selectors";
import {
    affectiveAreasSkills,
    psychomotorSkills,
} from "../../../shared/components/classes/studentReportCard/BehavourialSkill";

import PageLoader from "../../../shared/components/PageLoader";
import useFetchCalendarById from "../../../shared/api/calendar/useFetchCalendarById";
import useFetchStudentBasicInfo from "../../../shared/api/hooks/useFetchStudentBasicInfo";
import useFetchStudentReportCard from "../../../shared/api/cards/useFetchStudentReportCard";
import ReportCard from "../../../shared/components/classes/studentPreviewReportCard/ReportCard";
import ReportCard2 from "../../../shared/components/classes/studentPreviewReportCard/ReportCard2";
import ReportCard3 from "../../../shared/components/classes/studentPreviewReportCard/ReportCard3";
import useFetchStudentAttendance from "../../../shared/api/classes/attendance/useFetchStudentAttendance";

const StudentPreviewReportCard = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const session = query.get("session");
    const term = query.get("term");

    const reportCardRef = useRef();
    const { studentId } = useParams();
    const navigate = useNavigate();
    const school = useSelector(selectSchoolDetails);

    const { loading: studentDetailsLoading, data: studentDetails } =
        useFetchStudentBasicInfo(studentId);

    const {
        reportCard,
        scorecards,
        firstTermScorecards,
        secondTermScorecards,
        loading: reportCardLoading,
    } = useFetchStudentReportCard(session, term);

    const { overview: attendanceOverview } = useFetchStudentAttendance(
        studentId,
        reportCard?.calendarId
    );

    const { calendar } = useFetchCalendarById(reportCard?.calendarId);

    function getPsychomotorSkills() {
        if (!reportCard) return;

        const _psychomotorPayload = psychomotorSkills?.map((item) => ({
            ...item,
            value: reportCard?.[item.key] || "",
        }));
        return _psychomotorPayload;
    }

    function getAffectiveAreasSkill() {
        if (!reportCard) return;

        const _affectiveAreasPayload = affectiveAreasSkills?.map((item) => ({
            ...item,
            value: reportCard?.[item.key] || "",
        }));
        return _affectiveAreasPayload;
    }

    if (reportCardLoading || studentDetailsLoading) {
        return (
            <PageLoader loading={reportCardLoading || studentDetailsLoading} />
        );
    }

    return (
        <div>
            <div className="page">
                <div className="mb-5 flex items-center justify-between">
                    <div className="page_title_container">
                        <h1 className="page_title">
                            {`${studentDetails?.lastName || ""} ${
                                studentDetails?.firstName || ""
                            }'s`}{" "}
                            Report Card
                        </h1>
                    </div>

                    <ReactToPrint
                        trigger={() => (
                            <button
                                className="flex items-center justify-center gap-2 bg-primary h-8 xxs:h-8 ssm:h-9 rounded-sm text-white px-3 min-w-[80px]"
                                onClick={() => {}}
                            >
                                <IoCloudDownloadOutline className="text-sm" />
                                <small>Download</small>
                            </button>
                        )}
                        content={() => reportCardRef.current}
                    />
                </div>

                <div className="bg-white p-3">
                    {/* <NavLink
            to={`/${schoolId}/classes/${classId}/students/${studentId}/reportcard?session=${session}&term=${term}`}
          >
            <button className="px-3 py-2 flex items-center gap-2 text-yankee_blue hover:bg-green-light_10 hover:text-primary">
              <BiArrowBack className="text-sm" />
              <small>Back to cards</small>
            </button>
          </NavLink> */}
                    <button
                        className="px-3 py-2 flex items-center gap-2 text-yankee_blue hover:bg-green-light_10 hover:text-primary"
                        onClick={() => navigate(-1)}
                    >
                        <BiArrowBack className="text-sm" />
                        <small>Back to cards</small>
                    </button>
                </div>

                {term === "first" ? (
                    <div className="main_content">
                        <ReportCard
                            school={school}
                            ref={reportCardRef}
                            getGrade={getGrade}
                            subjects={scorecards}
                            getRemark={getRemark}
                            reportCard={reportCard}
                            vacationDate={new Date(
                                calendar?.vacationStartDate
                            )?.toDateString()}
                            nextResumptionDate={new Date(
                                calendar?.nextResumptionDate
                            )?.toDateString()}
                            attendanceOverview={attendanceOverview}
                            endSessionRemark={getStudentClassStatus(
                                reportCard?.studentClassHistory,
                                "end"
                            )}
                            psychomotorsKils={getPsychomotorSkills()}
                            affectiveAreasSkill={getAffectiveAreasSkill()}
                            closingDate={new Date(
                                calendar?.closingDate
                            )?.toDateString()}
                        />
                    </div>
                ) : null}

                {term === "second" ? (
                    <div className="main_content">
                        <ReportCard2
                            school={school}
                            ref={reportCardRef}
                            getGrade={getGrade}
                            subjects={scorecards}
                            getRemark={getRemark}
                            reportCard={reportCard}
                            getSubjectFromcards={getSubjectFromcards}
                            vacationDate={new Date(
                                calendar?.vacationStartDate
                            )?.toDateString()}
                            nextResumptionDate={new Date(
                                calendar?.nextResumptionDate
                            )?.toDateString()}
                            endSessionRemark={getStudentClassStatus(
                                reportCard?.studentClassHistory,
                                "end"
                            )}
                            firstTermScorecards={firstTermScorecards}
                            closingDate={new Date(
                                calendar?.closingDate
                            )?.toDateString()}
                            psychomotorsKils={getPsychomotorSkills()}
                            affectiveAreasSkill={getAffectiveAreasSkill()}
                            attendanceOverview={attendanceOverview}
                        />
                    </div>
                ) : null}

                {term === "third" ? (
                    <div className="main_content">
                        <ReportCard3
                            school={school}
                            ref={reportCardRef}
                            getGrade={getGrade}
                            subjects={scorecards}
                            getRemark={getRemark}
                            reportCard={reportCard}
                            getSubjectFromcards={getSubjectFromcards}
                            vacationDate={new Date(
                                calendar?.vacationStartDate
                            )?.toDateString()}
                            nextResumptionDate={new Date(
                                calendar?.nextResumptionDate
                            )?.toDateString()}
                            endSessionRemark={getStudentClassStatus(
                                reportCard?.studentClassHistory,
                                "end"
                            )}
                            firstTermScorecards={firstTermScorecards}
                            secondTermScorecards={secondTermScorecards}
                            closingDate={new Date(
                                calendar?.closingDate
                            )?.toDateString()}
                            psychomotorsKils={getPsychomotorSkills()}
                            affectiveAreasSkill={getAffectiveAreasSkill()}
                            attendanceOverview={attendanceOverview}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default StudentPreviewReportCard;
